const parseBooleanFlag = (name, value, defaultValue) => {
  if (typeof value === 'undefined') return defaultValue;
  if (value === true || value === 'true' || value === '1') return true;
  if (value === false || value === 'false' || value === '0') return false;
  throw new Error(`${name} must be one of: true, false, 1, 0`);
};

const parseLocaleListFlag = (name, value, allowedLocales) => {
  if (typeof value === 'undefined' || value === '') return [];
  if (typeof value !== 'string') {
    throw new Error(`${name} must be a comma-separated locale list`);
  }

  const locales = value.split(',');
  const invalidLocales = locales.filter((locale) => !allowedLocales.includes(locale));
  if (invalidLocales.length) {
    throw new Error(`${name} must contain only: ${allowedLocales.join(',')}`);
  }
  if (new Set(locales).size !== locales.length) {
    throw new Error(`${name} must not contain duplicate locales`);
  }

  return locales;
};

const featureAvailability = (magazine, ai, destinationCategoryIndexing) =>
  Object.freeze({ magazine, ai, destinationCategoryIndexing });

export const LOCALE_METADATA = Object.freeze({
  en: Object.freeze({
    bcp47: 'en-US',
    dateFnsLocale: 'enUS',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'MMM dd, yyyy',
    languageNameKey: 'en_language',
    nativeName: 'English',
    defaultCurrency: 'usd',
    destinationIndex: 'destinations_popularity_en',
    allAttractionsSlug: 'top-attractions',
    publicByDefault: true,
    features: featureAvailability(true, true, true),
  }),
  de: Object.freeze({
    bcp47: 'de-DE',
    dateFnsLocale: 'de',
    dateFormatShort: 'E d. MMM',
    dateFormatReview: 'dd MMM. yyyy',
    languageNameKey: 'de_language',
    nativeName: 'Deutsch',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_de',
    allAttractionsSlug: 'top-sehenswuerdigkeiten',
    publicByDefault: true,
    features: featureAvailability(true, true, true),
  }),
  it: Object.freeze({
    bcp47: 'it-IT',
    dateFnsLocale: 'it',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'dd MMM yyyy',
    languageNameKey: 'it_language',
    nativeName: 'Italiano',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_it',
    allAttractionsSlug: 'top-attrazioni',
    publicByDefault: true,
    features: featureAvailability(true, false, true),
  }),
  es: Object.freeze({
    bcp47: 'es-ES',
    dateFnsLocale: 'es',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'dd MMM yyyy',
    languageNameKey: 'es_language',
    nativeName: 'Español',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_es',
    allAttractionsSlug: 'principales-atracciones',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  fr: Object.freeze({
    bcp47: 'fr-FR',
    dateFnsLocale: 'fr',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'dd MMM yyyy',
    languageNameKey: 'fr_language',
    nativeName: 'Français',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_fr',
    allAttractionsSlug: 'principales-attractions',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  pl: Object.freeze({
    bcp47: 'pl-PL',
    dateFnsLocale: 'pl',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'd MMM yyyy',
    languageNameKey: 'pl_language',
    nativeName: 'Polski',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_pl',
    allAttractionsSlug: 'najwazniejsze-atrakcje',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  pt: Object.freeze({
    bcp47: 'pt-PT',
    dateFnsLocale: 'pt',
    dateFormatShort: 'E, d MMM',
    dateFormatReview: "d 'de' MMM 'de' yyyy",
    languageNameKey: 'pt_language',
    nativeName: 'Português',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_pt',
    allAttractionsSlug: 'principais-atracoes',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  cs: Object.freeze({
    bcp47: 'cs-CZ',
    dateFnsLocale: 'cs',
    dateFormatShort: 'E d. M.',
    dateFormatReview: 'd. MMM yyyy',
    languageNameKey: 'cs_language',
    nativeName: 'Čeština',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_cs',
    allAttractionsSlug: 'nejlepsi-atrakce',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  hu: Object.freeze({
    bcp47: 'hu-HU',
    dateFnsLocale: 'hu',
    dateFormatShort: 'MMM d., E',
    dateFormatReview: 'yyyy. MMM d.',
    languageNameKey: 'hu_language',
    nativeName: 'Magyar',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_hu',
    allAttractionsSlug: 'legjobb-latnivalok',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  nl: Object.freeze({
    bcp47: 'nl-NL',
    dateFnsLocale: 'nl',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'd MMM yyyy',
    languageNameKey: 'nl_language',
    nativeName: 'Nederlands',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_nl',
    allAttractionsSlug: 'topattracties',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  tr: Object.freeze({
    bcp47: 'tr-TR',
    dateFnsLocale: 'tr',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'd MMM yyyy',
    languageNameKey: 'tr_language',
    nativeName: 'Türkçe',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_tr',
    allAttractionsSlug: 'en-iyi-gezilecek-yerler',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
  ro: Object.freeze({
    bcp47: 'ro-RO',
    dateFnsLocale: 'ro',
    dateFormatShort: 'E d MMM',
    dateFormatReview: 'd MMM yyyy',
    languageNameKey: 'ro_language',
    nativeName: 'Română',
    defaultCurrency: 'eur',
    destinationIndex: 'destinations_popularity_ro',
    allAttractionsSlug: 'atractii-principale',
    publicByDefault: false,
    features: featureAvailability(false, false, false),
  }),
});

export const SUPPORTED_LOCALES = Object.freeze(Object.keys(LOCALE_METADATA));
export const ROLLOUT_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter((locale) => !LOCALE_METADATA[locale].publicByDefault),
);
// Tour translations are enrichment, not an inventory gate, for these rollout locales.
// Keep this field-level fallback policy independent of the global Algolia data fallback so route
// identity, indexing decisions, and other SEO-critical data continue to require exact localization.
export const TOUR_ENGLISH_FALLBACK_LOCALES = Object.freeze(['es', 'fr', 'pl', 'pt', 'cs', 'hu', 'nl', 'tr', 'ro']);
export const supportsTourEnglishFallback = (locale) =>
  TOUR_ENGLISH_FALLBACK_LOCALES.includes(locale);
const requestedSeoTourLocalizationLocales = parseLocaleListFlag(
  'SEO_TOUR_LOCALIZATION_LOCALES',
  process.env.SEO_TOUR_LOCALIZATION_LOCALES,
  ROLLOUT_LOCALES,
);
export const ENABLE_NEW_LOCALES_PUBLIC = parseBooleanFlag(
  'ENABLE_NEW_LOCALES_PUBLIC',
  process.env.ENABLE_NEW_LOCALES_PUBLIC,
  false,
);
const requestedPublicRolloutLocales = parseLocaleListFlag(
  'PUBLIC_ROLLOUT_LOCALES',
  process.env.PUBLIC_ROLLOUT_LOCALES,
  ROLLOUT_LOCALES,
);

if (ENABLE_NEW_LOCALES_PUBLIC && requestedPublicRolloutLocales.length) {
  throw new Error('ENABLE_NEW_LOCALES_PUBLIC cannot be combined with PUBLIC_ROLLOUT_LOCALES');
}

export const PUBLIC_ROLLOUT_LOCALES = Object.freeze(
  ENABLE_NEW_LOCALES_PUBLIC
    ? [...ROLLOUT_LOCALES]
    : ROLLOUT_LOCALES.filter((locale) => requestedPublicRolloutLocales.includes(locale)),
);
// Public rollout locales always receive enrichment for their exact SEO-eligible pages. The
// explicit selection remains additive so translations can be checked before public activation.
// Preserve registry order so the generated selection manifest is stable for equivalent inputs.
export const SEO_TOUR_LOCALIZATION_LOCALES = Object.freeze(
  ROLLOUT_LOCALES.filter(
    (locale) =>
      PUBLIC_ROLLOUT_LOCALES.includes(locale) ||
      requestedSeoTourLocalizationLocales.includes(locale),
  ),
);
export const ALLOW_ENGLISH_ALGOLIA_FALLBACK = parseBooleanFlag(
  'ALLOW_ENGLISH_ALGOLIA_FALLBACK',
  process.env.ALLOW_ENGLISH_ALGOLIA_FALLBACK,
  true,
);

if (PUBLIC_ROLLOUT_LOCALES.length && ALLOW_ENGLISH_ALGOLIA_FALLBACK) {
  throw new Error(
    'Public rollout locales cannot be enabled while ALLOW_ENGLISH_ALGOLIA_FALLBACK is enabled',
  );
}

export const CONTENT_OVERLAY_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter((locale) => locale !== 'en'),
);
export const MAGAZINE_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter((locale) => LOCALE_METADATA[locale].features.magazine),
);
export const AI_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter((locale) => LOCALE_METADATA[locale].features.ai),
);
export const DESTINATION_CATEGORY_INDEXING_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter(
    (locale) => LOCALE_METADATA[locale].features.destinationCategoryIndexing,
  ),
);
// Destination landing pages are an independently reviewed page type. Keep this
// allowlist explicit so publishing a new rollout locale cannot expose destination
// landings (or their retired see-all aliases) by default.
export const DESTINATION_PAGE_INDEXING_LOCALES = Object.freeze(['en', 'de', 'it', 'es']);
// Locale landing, destinations-index, and imprint pages require their own reviewed rollout.
// Keep this explicit so making a locale public for POIs cannot publish global pages by default.
export const GLOBAL_PAGE_INDEXING_LOCALES = Object.freeze(['en', 'de', 'it', 'es']);
// POI tour sections are a separate SEO page type with independent editorial requirements.
export const POI_TOURS_SECTION_INDEXING_LOCALES = Object.freeze(['en', 'de', 'it', 'es']);
// Legal approval waivers are valid only for locales outside this immutable page-type ceiling.
export const LEGAL_PAGE_INDEXING_LOCALES = Object.freeze(['en', 'de', 'it']);
export const PUBLIC_LOCALES = Object.freeze(
  SUPPORTED_LOCALES.filter(
    (locale) => LOCALE_METADATA[locale].publicByDefault || PUBLIC_ROLLOUT_LOCALES.includes(locale),
  ),
);

export const LOCALES = SUPPORTED_LOCALES;
export const DEFAULT_LOCALE = 'en';
export const isSupportedLocale = (locale) => SUPPORTED_LOCALES.includes(locale);
export const isPublicLocale = (locale) => PUBLIC_LOCALES.includes(locale);
// Content launch holds, independent of visibility and search-indexing switches.
// Source: content/docs/tr-ro-launch-exclusions.json.
export const LOCALE_LAUNCH_EXCLUDED_POIS = Object.freeze(['20575913', '900481009', '900481010', '900481011', '266012']);
export const isLocaleLaunchExcludedPoi = (locale, poiId) =>
  ['tr', 'ro'].includes(locale) && LOCALE_LAUNCH_EXCLUDED_POIS.includes(String(poiId));
export const getLocaleMetadata = (locale) =>
  LOCALE_METADATA[locale] || LOCALE_METADATA[DEFAULT_LOCALE];
export const getDestinationIndex = (locale) => getLocaleMetadata(locale).destinationIndex;
export const getAllAttractionsSlug = (locale) => getLocaleMetadata(locale).allAttractionsSlug;
export const getBcp47Locale = (locale) =>
  (LOCALE_METADATA[locale] && LOCALE_METADATA[locale].bcp47) || locale || LOCALE_METADATA.en.bcp47;
